import { LitElement, html, type PropertyValues } from 'lit'
import { consume } from '@lit/context'
import { customElement, property, state } from 'lit/decorators.js'

import { TW } from '../../shared'
import type { MenuItem as MenuItemType } from '../../shared'
import { navigationContext } from './context.ts'

const TwLitElement = TW(LitElement)

/**
 * Sample Lit Element in assets/lit
 */
@customElement('menu-item')
export class MenuItem extends TwLitElement {

    @property({ type: Number, attribute: 'id' }) 
    menuItemId: number = 0

    @property({ type: String, attribute: 'aria-label' })
    ariaLabel: string = ''

    @property({ type: String, attribute: 'class-default' })
    class: string = 'font-normal'

    @property({ type: String, attribute: 'class-active' })
    classActive: string = 'font-normal'

    @property({ type: String, attribute: 'sub-menu-icon' })
    subMenuIcon: string = 'bmw-icon-arrow-chevron-right-rg'

    @property({ type: String, attribute: 'sub-menu-icon-position' })
    subMenuIconPosition: string = 'after'

    @property({ type: Boolean, attribute: 'also-toggle-sub-menu' })
    alsoToggleSubMenu: boolean = false

    @consume({context: navigationContext, subscribe: true}) 
    private _data: MenuItemType[] = []

    @state()
    private _menuItem: MenuItemType | null = null

    willUpdate(changedProperties: PropertyValues) 
    {
        // Initialisiere den _data Status mit den Daten aus der Property data und füge das active property hinzu
        
        if ( changedProperties.has('menuItemId') ) {
            this._menuItem = this._data.find( item => item.id === this.menuItemId ) ?? null
        }

    }

    private _onClick(event: Event) 
    {
        event.preventDefault();
        event.stopPropagation();
        this.dispatchEvent( new CustomEvent( 'bmw-header-toggle-menu-item', { detail: { navItem: this._menuItem, alsoToggleSubMenu: this.alsoToggleSubMenu }, bubbles: true, composed: true } ) )
    }

    render() 
    {
        const menuItem = this._data.find( item => item.id === this.menuItemId ) ?? null        

        if ( menuItem === null ) {
            return html``
        }

        return menuItem.hasChildren 
            ? html`<a 
                href=${menuItem.url} 
                title=${menuItem.title} 
                @click=${( event: Event ) => this._onClick(event)} 
                ${ this.ariaLabel ? `aria-label=${this.ariaLabel}` : '' } 
                class="no-underline inline-flex gap-0.5 items-center ${menuItem.active ? this.classActive : this.class}"
                ${ menuItem.target === '_blank' ? `target=${menuItem.target}` : '' }
            >
                ${ 
                    this.subMenuIconPosition === 'before' 
                    ? html`<span class="bmw-icon ${this.subMenuIcon} before:text-sm"></span>` 
                    : html`` 
                }
                ${menuItem.title}
                ${ 
                    this.subMenuIconPosition === 'after' 
                    ? html`<span class="bmw-icon ${this.subMenuIcon} before:text-sm"></span>` 
                    : html`` 
                }
            </a>`
            : html`<a href=${menuItem.url} target="${menuItem.target ?? '_self'}" title=${menuItem.title} class="no-underline inline-flex gap-1 items-center ${menuItem.active ? this.classActive : this.class}">
                ${menuItem.title}
                ${
                    menuItem.target === '_blank'
                    ? html`<span class="bmw-icon bmw-icon-arrow-up-right-rg before:text-xxs before:text-neutral-light"></span>`
                    : html``
                }
            </a>`
        ;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'menu-item': MenuItem
    }
}
